import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ChefHat, Clock, CheckCircle, AlertTriangle,
    RefreshCcw, Timer, Bell, Flame, Check,
    AlertCircle, Package, X, Play, Maximize2, Minimize2,
    Volume2, VolumeX, Utensils, AlertOctagon, Home, Users, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = () => {
    const token = localStorage.getItem('token');
    return {
        get: async (url) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        },
        put: async (url, data) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        },
        post: async (url, data) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        },
        delete: async (url) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        }
    };
};

// ========================================
// TICKET DE COMMANDE CUISINE - Design Compact
// ========================================

const KitchenTicket = ({ 
    order, 
    onCommencer, 
    onPret, 
    onProbleme, 
    isFullscreen = false,
    showCommencer = false,
    showPret = false,
    isReady = false,
    canAct = true
}) => {
    const getElapsedMinutes = () => {
        const startTime = order.sent_to_kitchen_at || order.created_at;
        if (!startTime) return 0;
        const start = new Date(startTime);
        const now = new Date();
        return Math.floor((now - start) / 60000);
    };

    const elapsedMinutes = getElapsedMinutes();
    
    const getTimeColor = () => {
        if (elapsedMinutes < 5) return 'text-green-400';
        if (elapsedMinutes < 10) return 'text-orange-400';
        return 'text-red-400';
    };

    const getBorderColor = () => {
        if (isReady) return 'border-green-500 bg-green-50';
        if (elapsedMinutes >= 10) return 'border-red-500 bg-red-50 animate-pulse';
        if (elapsedMinutes >= 5) return 'border-orange-400 bg-orange-50';
        return 'border-gray-700 bg-white';
    };

    const getHeaderBg = () => {
        if (isReady) return 'bg-green-600';
        if (elapsedMinutes >= 10) return 'bg-red-600';
        if (elapsedMinutes >= 5) return 'bg-orange-500';
        return 'bg-gray-800';
    };

    const formatArrivalTime = () => {
        const time = order.sent_to_kitchen_at || order.created_at;
        if (!time) return '-';
        return new Date(time).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Africa/Libreville'
        });
    };

    return (
        <div 
            className={cn(
                "rounded-xl border-2 overflow-hidden shadow-lg transition-all duration-300",
                getBorderColor()
            )}
        >
            {/* Header compact - PRIORITÉ: Serveur, Destination, Heure */}
            <div className={cn("px-3 py-2 text-white", getHeaderBg())}>
                {/* 1. SERVEUR - En premier, bien visible */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                            <ChefHat className="w-4 h-4 text-white" />
                        </div>
                        <span className={cn("font-black text-white", isFullscreen ? "text-xl" : "text-base")}>
                            {order.waiter_name || order.server_name || 'Serveur'}
                        </span>
                    </div>
                    
                    <div className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded-lg font-mono font-bold bg-black/30",
                        getTimeColor()
                    )}>
                        <Timer className="w-3.5 h-3.5" />
                        <span className={isFullscreen ? "text-lg" : "text-sm"}>
                            {elapsedMinutes}m
                        </span>
                        {elapsedMinutes >= 10 && (
                            <Flame className="w-4 h-4 text-yellow-300 animate-bounce ml-1" />
                        )}
                    </div>
                </div>
                
                {/* 2. DESTINATION + HEURE */}
                <div className="flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-2">
                        {/* Table/Destination */}
                        <Badge variant="secondary" className="bg-white/25 text-white text-xs px-2 py-0.5 font-semibold">
                            {order.table_number ? (
                                <>
                                    <Users className="w-3 h-3 mr-1 inline" />
                                    Table {order.table_number}
                                    {order.salle && ` • ${order.salle}`}
                                </>
                            ) : 'Comptoir'}
                        </Badge>
                        
                        {/* Badge À EMPORTER - très visible */}
                        {(order.is_takeaway || order.order_type === 'takeaway') && (
                            <Badge className="bg-yellow-500 text-black font-black text-xs px-2 py-1 animate-pulse">
                                📦 À EMPORTER
                            </Badge>
                        )}
                    </div>
                    
                    {/* 3. HEURE */}
                    <div className="flex items-center gap-1 text-white/80 text-xs">
                        <Clock className="w-3 h-3" />
                        <span className="font-medium">{formatArrivalTime()}</span>
                    </div>
                </div>
                
                {/* Numéro technique - petit, en bas */}
                <div className="text-white/50 text-xs mt-1 text-right">
                    Cmd #{order.order_number?.split('-').pop() || order.order_id?.slice(-4)}
                </div>
            </div>

            {/* Items - Liste compacte */}
            <div className="px-3 py-2 bg-white">
                <div className="space-y-1.5">
                    {order.items?.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                            <span className={cn(
                                "inline-flex items-center justify-center min-w-[24px] h-6 rounded bg-gray-800 text-white font-bold",
                                isFullscreen ? "text-sm min-w-[28px]" : "text-xs"
                            )}>
                                {item.quantity || 1}x
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className={cn(
                                    "font-semibold text-gray-800",
                                    isFullscreen ? "text-base" : "text-sm"
                                )}>
                                    {item.name || item.product_name}
                                </div>
                                {/* Formula parent mention */}
                                {item.is_formula_item && item.formula_name && (
                                    <div className="text-xs text-purple-600 font-medium flex items-center gap-1">
                                        <Utensils className="w-3 h-3" />
                                        {item.formula_name}
                                    </div>
                                )}
                                {/* Supplement badge */}
                                {item.is_supplement && (
                                    <div className="inline-flex items-center text-[10px] text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded mt-0.5">
                                        SUPPLÉMENT
                                    </div>
                                )}
                                {item.accompaniment?.name && (
                                    <div className="text-xs text-gray-500 truncate">
                                        + {item.accompaniment.name}
                                    </div>
                                )}
                                {item.supplements?.length > 0 && (
                                    <div className="text-xs text-blue-600 truncate">
                                        + {item.supplements.map(s => s.name || s).join(', ')}
                                    </div>
                                )}
                                {item.notes && (
                                    <div className="text-xs text-orange-600 italic flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">{item.notes}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {order.notes && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded-lg border border-yellow-200 text-xs">
                        <strong>Note:</strong> {order.notes}
                    </div>
                )}
            </div>

            {/* Actions */}
            {canAct ? (
                (showCommencer || showPret) && (
                    <div className="px-3 py-2 bg-gray-50 border-t flex gap-2">
                        {showCommencer && (
                            <Button 
                                onClick={() => onCommencer(order)}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 h-9"
                                size="sm"
                            >
                                <Play className="w-4 h-4 mr-1" />
                                Commencer
                            </Button>
                        )}
                        {showPret && (
                            <Button 
                                onClick={() => onPret(order)}
                                className="flex-1 bg-green-600 hover:bg-green-700 h-9"
                                size="sm"
                            >
                                <Check className="w-4 h-4 mr-1" />
                                PRÊT
                            </Button>
                        )}
                        <Button 
                            onClick={() => onProbleme(order)}
                            variant="outline"
                            className="border-red-400 text-red-600 hover:bg-red-50 h-9 px-2"
                            size="sm"
                        >
                            <AlertOctagon className="w-4 h-4" />
                        </Button>
                    </div>
                )
            ) : (
                <div className="px-3 py-2 border-t bg-gray-100 text-center text-gray-500 text-xs">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Mode lecture seule
                </div>
            )}
        </div>
    );
};

// ========================================
// COMPOSANT PRINCIPAL - ÉCRAN CUISINE
// ========================================

const KitchenDashboardPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    // Permissions - Qui peut voir et qui peut agir
    // SERVEUR a accès en lecture seule (consultation)
    const allowedRoles = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE', 'CUISINIER', 'SERVEUR', 'SERVEUSE'];
    const canActRoles = ['ADMIN', 'CHEF_CUISINE', 'CUISINIER']; // Seuls ces rôles peuvent valider/modifier
    const canAct = user && canActRoles.includes(user.role);
    const hasAccess = user && allowedRoles.includes(user.role);
    // Mode lecture seule pour Directeurs ET Serveurs
    const isReadOnlyMode = user && ['DIRECTEUR', 'DIRECTEUR_ADJOINT', 'SERVEUR', 'SERVEUSE'].includes(user.role);
    
    // États - TOUS les hooks doivent être avant tout return conditionnel
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [readyOrdersToRemove, setReadyOrdersToRemove] = useState(new Set());
    
    // Settings for auto-dismiss
    const [settings, setSettings] = useState({
        readyOrdersAutoDismiss: true,
        readyOrdersDismissDelay: 30,  // seconds
        readyOrdersShowServedColumn: true
    });
    
    // Dialog problème
    const [problemDialog, setProblemDialog] = useState({ open: false, order: null });
    const [problemNote, setProblemNote] = useState('');
    
    // Messages de la salle
    const [kitchenMessages, setKitchenMessages] = useState([]);
    const [showMessagesPanel, setShowMessagesPanel] = useState(true);
    
    // Stats
    const [stats, setStats] = useState({
        avgPrepTime: 0,
        completedToday: 0
    });
    
    const intervalRef = useRef(null);
    const clockRef = useRef(null);
    const audioRef = useRef(null);
    const dismissTimersRef = useRef(new Map());  // Track dismiss timers

    // Heure du Gabon (UTC+1)
    const getGabonTime = () => {
        return new Date().toLocaleTimeString('fr-FR', {
            timeZone: 'Africa/Libreville',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // Fetch settings
    const fetchSettings = useCallback(async () => {
        if (!hasAccess) return;
        try {
            const response = await api().get('/pos/settings');
            if (response.data) {
                setSettings(prev => ({
                    ...prev,
                    readyOrdersAutoDismiss: response.data.readyOrdersAutoDismiss ?? true,
                    readyOrdersDismissDelay: response.data.readyOrdersDismissDelay ?? 30,
                    readyOrdersShowServedColumn: response.data.readyOrdersShowServedColumn ?? true
                }));
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    }, [hasAccess]);

    // Récupération des commandes
    const fetchOrders = useCallback(async () => {
        if (!hasAccess) return;
        try {
            const response = await api().get('/kitchen/tickets');
            const newOrders = response.data || [];
            
            // Détecter les nouvelles commandes pour notification sonore
            if (soundEnabled && orders.length > 0) {
                const oldIds = new Set(orders.map(o => o.order_id));
                const hasNew = newOrders.some(o => !oldIds.has(o.order_id) && o.kitchen_status === 'nouveau');
                if (hasNew && audioRef.current) {
                    audioRef.current.play().catch(() => {});
                }
            }
            
            setOrders(newOrders);
            
            // Récupérer les stats
            try {
                const statsRes = await api().get('/kitchen/stats');
                setStats({
                    avgPrepTime: statsRes.data.avg_prep_time_minutes || statsRes.data.temps_moyen_minutes || 0,
                    completedToday: statsRes.data.orders_completed_today || 0
                });
            } catch (e) {}
            
            // Récupérer les messages de la salle (ancien système)
            try {
                const messagesRes = await api().get('/tables/kitchen-messages?limit=10');
                const oldMessages = messagesRes.data || [];
                
                // Récupérer aussi les messages instantanés (nouveau système)
                let newMessages = [];
                try {
                    const instantMsgsRes = await api().get('/kitchen/messages?status=pending');
                    newMessages = (instantMsgsRes.data || []).map(m => ({
                        ...m,
                        isInstantMessage: true, // Flag pour distinguer
                        message: m.message,
                        table_number: m.table_number,
                        sender_name: m.sender_name,
                        created_at: m.created_at,
                        priority: m.priority
                    }));
                } catch (e) {
                    console.warn('No instant messages endpoint:', e);
                }
                
                // Combiner les deux sources de messages
                setKitchenMessages([...newMessages, ...oldMessages]);
            } catch (e) {
                console.error('Error fetching kitchen messages:', e);
            }
            
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    }, [soundEnabled, orders.length, hasAccess]);

    useEffect(() => {
        if (!hasAccess) return;
        fetchSettings();
        fetchOrders();
        intervalRef.current = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
        clockRef.current = setInterval(() => setCurrentTime(new Date()), 1000);
        
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (clockRef.current) clearInterval(clockRef.current);
            // Clear all dismiss timers
            dismissTimersRef.current.forEach(timer => clearTimeout(timer));
        };
    }, [fetchOrders, fetchSettings, hasAccess]);

    // Auto-suppression des commandes prêtes avec délai configurable
    useEffect(() => {
        if (!hasAccess || !settings.readyOrdersAutoDismiss) return;
        
        const readyOrders = orders.filter(o => 
            (o.kitchen_status === 'pret' || o.kitchen_status === 'servi') && 
            !readyOrdersToRemove.has(o.order_id)
        );
        
        readyOrders.forEach(order => {
            // Skip if timer already exists
            if (dismissTimersRef.current.has(order.order_id)) return;
            
            const timer = setTimeout(() => {
                setReadyOrdersToRemove(prev => new Set([...prev, order.order_id]));
                dismissTimersRef.current.delete(order.order_id);
            }, settings.readyOrdersDismissDelay * 1000);  // Convert to milliseconds
            
            dismissTimersRef.current.set(order.order_id, timer);
        });
    }, [orders, readyOrdersToRemove, settings.readyOrdersAutoDismiss, settings.readyOrdersDismissDelay, hasAccess]);

    // Actions
    const handleCommencer = async (order) => {
        if (!canAct) return;
        try {
            await api().put(`/kitchen/tickets/${order.order_id}`, { status: 'en_preparation' });
            toast.success(`Commande #${order.order_number?.slice(-4) || order.order_id?.slice(-4)} en préparation`);
            fetchOrders();
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors du démarrage');
        }
    };

    const handlePret = async (order) => {
        if (!canAct) return;
        try {
            await api().put(`/kitchen/tickets/${order.order_id}`, { status: 'pret' });
            if (audioRef.current) {
                audioRef.current.play().catch(() => {});
            }
            toast.success(`Commande #${order.order_number?.slice(-4) || order.order_id?.slice(-4)} PRÊTE !`);
            fetchOrders();
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de la validation');
        }
    };

    const handleProbleme = (order) => {
        if (!canAct) return;
        setProblemDialog({ open: true, order });
        setProblemNote('');
    };

    const submitProbleme = async () => {
        if (!canAct || !problemDialog.order) return;
        try {
            await api().put(`/kitchen/tickets/${problemDialog.order.order_id}`, { 
                status: 'probleme',
                notes: problemNote 
            });
            toast.warning('Problème signalé');
            setProblemDialog({ open: false, order: null });
            setProblemNote('');
            fetchOrders();
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur');
        }
    };

    const handleReprendre = async (order) => {
        if (!canAct) return;
        try {
            await api().put(`/kitchen/tickets/${order.order_id}`, { status: 'en_preparation' });
            fetchOrders();
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    // Marquer un message comme lu
    const markMessageAsRead = async (messageId) => {
        try {
            await api().put(`/tables/kitchen-messages/${messageId}/read`);
            setKitchenMessages(prev => prev.map(m => 
                m.id === messageId ? { ...m, read: true } : m
            ));
        } catch (error) {
            console.error('Error marking message as read:', error);
        }
    };

    // Supprimer un message
    const deleteMessage = async (messageId) => {
        try {
            await api().delete(`/tables/kitchen-messages/${messageId}`);
            setKitchenMessages(prev => prev.filter(m => m.id !== messageId));
            toast.success('Message supprimé');
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Erreur');
        }
    };

    // Fullscreen toggle
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };
    
    // Vérifier les permissions d'accès - APRÈS tous les hooks
    if (!hasAccess) {
        return (
            <div className="h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center text-white">
                    <ChefHat className="w-16 h-16 mx-auto mb-4 text-orange-400" />
                    <h1 className="text-2xl font-bold mb-2">Accès Restreint</h1>
                    <p className="text-gray-400">Écran réservé au personnel de cuisine</p>
                    <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => navigate('/dashboard')}
                    >
                        Retour
                    </Button>
                </div>
            </div>
        );
    }

    // Filtrage des commandes
    const pendingOrders = orders.filter(o => 
        o.kitchen_status === 'nouveau' || o.kitchen_status === 'en_attente'
    );
    const preparingOrders = orders.filter(o => 
        o.kitchen_status === 'en_preparation'
    );
    const readyOrders = orders.filter(o => 
        (o.kitchen_status === 'pret' || o.kitchen_status === 'ready') && 
        !readyOrdersToRemove.has(o.order_id)
    );
    const problemOrders = orders.filter(o => 
        o.kitchen_status === 'probleme'
    );
    
    // Commandes urgentes (> 10 min)
    const urgentOrders = orders.filter(o => {
        const startTime = o.sent_to_kitchen_at || o.created_at;
        if (!startTime) return false;
        const elapsed = (new Date() - new Date(startTime)) / 60000;
        return elapsed >= 10 && o.kitchen_status !== 'pret' && o.kitchen_status !== 'servi';
    });

    // Calcul temps moyen réel (seulement les commandes terminées aujourd'hui)
    const avgPrepTime = stats.avgPrepTime || 0;

    return (
        <div className={cn(
            "min-h-screen bg-gray-100",
            isFullscreen ? "p-4" : "p-4"
        )}>
            {/* Audio pour notifications */}
            <audio ref={audioRef} preload="auto">
                <source src="/notification.mp3" type="audio/mpeg" />
            </audio>

            {/* Header - Responsive */}
            <div className={cn(
                "bg-gray-900 text-white rounded-xl sm:rounded-2xl shadow-xl mb-2 sm:mb-4",
                "flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0",
                isFullscreen ? "p-6" : "p-3 sm:p-4"
            )}>
                <div className="flex items-center gap-2 sm:gap-4">
                    <ChefHat className={cn("text-amber-400", isFullscreen ? "w-12 h-12" : "w-6 h-6 sm:w-8 sm:h-8")} />
                    <div>
                        <h1 className={cn("font-black", isFullscreen ? "text-4xl" : "text-lg sm:text-2xl")}>
                            CUISINE
                        </h1>
                        <p className="text-gray-400 text-xs sm:text-sm hidden sm:block">Système de production</p>
                    </div>
                    <Badge className="bg-green-600 text-white animate-pulse ml-2 sm:ml-4 text-xs">
                        LIVE
                    </Badge>
                    {/* Badge lecture seule pour DIRECTEUR et DIRECTEUR_ADJOINT */}
                    {isReadOnlyMode && (
                        <Badge className="bg-amber-600 text-white ml-2 flex items-center gap-1 text-xs">
                            <Eye className="h-3 w-3" />
                            Lecture seule
                        </Badge>
                    )}
                </div>

                <div className={cn(
                    "font-mono font-bold text-amber-400",
                    isFullscreen ? "text-6xl" : "text-2xl sm:text-4xl"
                )}>
                    {getGabonTime()}
                </div>

                <div className="flex items-center gap-2 sm:gap-4">
                    {/* Bouton Retour à l'accueil */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/dashboard')}
                        className="border-gray-600 text-white hover:bg-gray-800 h-8 sm:h-10 px-2 sm:px-3"
                        data-testid="home-btn"
                    >
                        <Home className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="ml-1 hidden sm:inline text-xs">Accueil</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSoundEnabled(!soundEnabled)}
                        className="border-gray-600 text-white hover:bg-gray-800 h-8 w-8 sm:h-10 sm:w-auto p-0 sm:px-3"
                    >
                        {soundEnabled ? <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleFullscreen}
                        className="border-gray-600 text-white hover:bg-gray-800 h-8 w-8 sm:h-10 sm:w-auto p-0 sm:px-3 hidden sm:flex"
                    >
                        {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchOrders}
                        className="border-gray-600 text-white hover:bg-gray-800 h-8 w-8 sm:h-10 sm:w-auto p-0 sm:px-3"
                    >
                        <RefreshCcw className={cn("w-4 h-4 sm:w-5 sm:h-5", loading && "animate-spin")} />
                    </Button>
                </div>
            </div>

            {/* Statistiques - Responsive */}
            <div className={cn(
                "grid gap-2 sm:gap-4 mb-2 sm:mb-4",
                "grid-cols-3 sm:grid-cols-6"
            )}>
                <div className={cn(
                    "bg-amber-500 text-white rounded-lg sm:rounded-xl shadow-lg flex items-center justify-center flex-col",
                    isFullscreen ? "p-6" : "p-2 sm:p-4"
                )}>
                    <span className={cn("font-black", isFullscreen ? "text-6xl" : "text-2xl sm:text-4xl")}>
                        {pendingOrders.length}
                    </span>
                    <span className={cn("font-bold text-center", isFullscreen ? "text-xl" : "text-[10px] sm:text-sm")}>ATTENTE</span>
                </div>
                <div className={cn(
                    "bg-blue-500 text-white rounded-lg sm:rounded-xl shadow-lg flex items-center justify-center flex-col",
                    isFullscreen ? "p-6" : "p-2 sm:p-4"
                )}>
                    <span className={cn("font-black", isFullscreen ? "text-6xl" : "text-2xl sm:text-4xl")}>
                        {preparingOrders.length}
                    </span>
                    <span className={cn("font-bold text-center", isFullscreen ? "text-xl" : "text-[10px] sm:text-sm")}>EN PRÉPA</span>
                </div>
                <div className={cn(
                    "bg-green-500 text-white rounded-lg sm:rounded-xl shadow-lg flex items-center justify-center flex-col",
                    isFullscreen ? "p-6" : "p-2 sm:p-4"
                )}>
                    <span className={cn("font-black", isFullscreen ? "text-6xl" : "text-2xl sm:text-4xl")}>
                        {readyOrders.length}
                    </span>
                    <span className={cn("font-bold text-center", isFullscreen ? "text-xl" : "text-[10px] sm:text-sm")}>PRÊTES</span>
                </div>
                <div className={cn(
                    "bg-red-500 text-white rounded-lg sm:rounded-xl shadow-lg flex items-center justify-center flex-col",
                    isFullscreen ? "p-6" : "p-2 sm:p-4",
                    urgentOrders.length > 0 && "animate-pulse"
                )}>
                    <span className={cn("font-black", isFullscreen ? "text-6xl" : "text-2xl sm:text-4xl")}>
                        {urgentOrders.length}
                    </span>
                    <span className={cn("font-bold text-center", isFullscreen ? "text-xl" : "text-[10px] sm:text-sm")}>RETARD</span>
                </div>
                <div className={cn(
                    "bg-purple-500 text-white rounded-lg sm:rounded-xl shadow-lg flex items-center justify-center flex-col",
                    isFullscreen ? "p-6" : "p-2 sm:p-4",
                    problemOrders.length > 0 && "animate-pulse"
                )}>
                    <span className={cn("font-black", isFullscreen ? "text-6xl" : "text-2xl sm:text-4xl")}>
                        {problemOrders.length}
                    </span>
                    <span className={cn("font-bold text-center", isFullscreen ? "text-xl" : "text-[10px] sm:text-sm")}>PROBLÈME</span>
                </div>
                <div className={cn(
                    "bg-gray-700 text-white rounded-lg sm:rounded-xl shadow-lg flex items-center justify-center flex-col",
                    isFullscreen ? "p-6" : "p-2 sm:p-4"
                )}>
                    <span className={cn("font-black text-emerald-400", isFullscreen ? "text-5xl" : "text-xl sm:text-3xl")}>
                        {avgPrepTime || '-'}
                    </span>
                    <span className={cn("font-bold text-center", isFullscreen ? "text-xl" : "text-[10px] sm:text-sm")}>TEMPS MOY</span>
                </div>
            </div>

            {/* Alerte urgente - Responsive */}
            {urgentOrders.length > 0 && (
                <div className={cn(
                    "bg-red-600 text-white rounded-lg sm:rounded-xl shadow-lg mb-2 sm:mb-4 flex items-center justify-center gap-2 sm:gap-4 animate-pulse",
                    isFullscreen ? "p-6" : "p-2 sm:p-4"
                )}>
                    <Flame className={cn("text-yellow-300", isFullscreen ? "w-10 h-10" : "w-5 h-5 sm:w-8 sm:h-8")} />
                    <span className={cn("font-black text-center", isFullscreen ? "text-3xl" : "text-sm sm:text-xl")}>
                        {urgentOrders.length} COMMANDE(S) EN RETARD !
                    </span>
                    <Flame className={cn("text-yellow-300 hidden sm:block", isFullscreen ? "w-10 h-10" : "w-8 h-8")} />
                </div>
            )}

            {/* Alerte problèmes - Responsive */}
            {problemOrders.length > 0 && (
                <div className={cn(
                    "bg-purple-600 text-white rounded-lg sm:rounded-xl shadow-lg mb-2 sm:mb-4 flex items-center justify-center gap-2 sm:gap-4",
                    isFullscreen ? "p-6" : "p-2 sm:p-4"
                )}>
                    <AlertOctagon className={cn("text-white", isFullscreen ? "w-10 h-10" : "w-5 h-5 sm:w-8 sm:h-8")} />
                    <span className={cn("font-black text-center", isFullscreen ? "text-3xl" : "text-sm sm:text-xl")}>
                        {problemOrders.length} COMMANDE(S) AVEC PROBLÈME
                    </span>
                    <AlertOctagon className={cn("text-white hidden sm:block", isFullscreen ? "w-10 h-10" : "w-8 h-8")} />
                </div>
            )}

            {/* ========================================
                MESSAGES DE LA SALLE - Cadre des demandes
            ======================================== */}
            {kitchenMessages.length > 0 && (
                <div className={cn(
                    "bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg sm:rounded-xl shadow-lg mb-2 sm:mb-4",
                    isFullscreen ? "p-4" : "p-3"
                )}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Bell className={cn("animate-bounce", isFullscreen ? "w-6 h-6" : "w-5 h-5")} />
                            <span className={cn("font-bold", isFullscreen ? "text-xl" : "text-sm sm:text-base")}>
                                MESSAGES DE LA SALLE ({kitchenMessages.filter(m => !m.read).length} non lu{kitchenMessages.filter(m => !m.read).length > 1 ? 's' : ''})
                            </span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowMessagesPanel(!showMessagesPanel)}
                            className="text-white hover:bg-white/20 h-7 px-2"
                        >
                            {showMessagesPanel ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </Button>
                    </div>
                    
                    {showMessagesPanel && (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                            {kitchenMessages.map(msg => (
                                <div 
                                    key={msg.id}
                                    className={cn(
                                        "bg-white/10 backdrop-blur rounded-lg p-2 sm:p-3 flex items-start gap-3",
                                        !msg.read && "bg-white/20 border-l-4 border-yellow-400"
                                    )}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Badge className="bg-amber-500 text-white text-xs px-2">
                                                Table {msg.table_number}
                                            </Badge>
                                            <span className="text-xs text-white/70 truncate">
                                                {msg.sender_name}
                                            </span>
                                            <span className="text-xs text-white/50">
                                                {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <p className={cn(
                                            "text-white font-medium",
                                            isFullscreen ? "text-base" : "text-sm"
                                        )}>
                                            {msg.message}
                                        </p>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        {!msg.read && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => markMessageAsRead(msg.id)}
                                                className="text-white hover:bg-white/20 h-7 w-7 p-0"
                                                title="Marquer comme lu"
                                            >
                                                <Check className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => deleteMessage(msg.id)}
                                            className="text-white hover:bg-red-500/50 h-7 w-7 p-0"
                                            title="Supprimer"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* VERSION MOBILE : Onglets */}
            <div className="sm:hidden">
                <Tabs defaultValue="attente" className="w-full">
                    <TabsList className="grid grid-cols-4 w-full mb-2">
                        <TabsTrigger value="attente" className="text-xs px-1 data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                            Attente ({pendingOrders.length})
                        </TabsTrigger>
                        <TabsTrigger value="preparation" className="text-xs px-1 data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                            Prépa ({preparingOrders.length})
                        </TabsTrigger>
                        <TabsTrigger value="pretes" className="text-xs px-1 data-[state=active]:bg-green-500 data-[state=active]:text-white">
                            Prêtes ({readyOrders.length})
                        </TabsTrigger>
                        <TabsTrigger value="problemes" className="text-xs px-1 data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                            Pb ({problemOrders.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="attente" className="mt-0">
                        <div className="bg-amber-50 rounded-lg border-2 border-amber-300 h-[calc(100vh-280px)] overflow-hidden">
                            <ScrollArea className="h-full p-2">
                                <div className="space-y-2">
                                    {pendingOrders.length === 0 ? (
                                        <div className="text-center py-8 text-amber-600/50">
                                            <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">Aucune commande</p>
                                        </div>
                                    ) : (
                                        pendingOrders.map(order => (
                                            <KitchenTicket key={order.order_id} order={order} isFullscreen={false}
                                                onCommencer={handleCommencer} onPret={handlePret} onProbleme={handleProbleme}
                                                showCommencer={true} showPret={false} canAct={canAct} />
                                        ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </TabsContent>

                    <TabsContent value="preparation" className="mt-0">
                        <div className="bg-blue-50 rounded-lg border-2 border-blue-300 h-[calc(100vh-280px)] overflow-hidden">
                            <ScrollArea className="h-full p-2">
                                <div className="space-y-2">
                                    {preparingOrders.length === 0 ? (
                                        <div className="text-center py-8 text-blue-600/50">
                                            <Utensils className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">Aucune préparation</p>
                                        </div>
                                    ) : (
                                        preparingOrders.map(order => (
                                            <KitchenTicket key={order.order_id} order={order} isFullscreen={false}
                                                onCommencer={handleCommencer} onPret={handlePret} onProbleme={handleProbleme}
                                                showCommencer={false} showPret={true} canAct={canAct} />
                                        ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </TabsContent>

                    <TabsContent value="pretes" className="mt-0">
                        <div className="bg-green-50 rounded-lg border-2 border-green-300 h-[calc(100vh-280px)] overflow-hidden">
                            <ScrollArea className="h-full p-2">
                                <div className="space-y-2">
                                    {readyOrders.length === 0 ? (
                                        <div className="text-center py-8 text-green-600/50">
                                            <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">Aucune commande prête</p>
                                        </div>
                                    ) : (
                                        readyOrders.map(order => (
                                            <KitchenTicket key={order.order_id} order={order} isFullscreen={false}
                                                onCommencer={handleCommencer} onPret={handlePret} onProbleme={handleProbleme}
                                                showCommencer={false} showPret={false} isReady={true} canAct={canAct} />
                                        ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </TabsContent>

                    <TabsContent value="problemes" className="mt-0">
                        <div className="bg-purple-50 rounded-lg border-2 border-purple-300 h-[calc(100vh-280px)] overflow-hidden">
                            <ScrollArea className="h-full p-2">
                                <div className="space-y-2">
                                    {problemOrders.length === 0 ? (
                                        <div className="text-center py-8 text-purple-600/50">
                                            <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">Aucun problème</p>
                                        </div>
                                    ) : (
                                        problemOrders.map(order => (
                                            <KitchenTicket key={order.order_id} order={order} isFullscreen={false}
                                                onCommencer={handleCommencer} onPret={handlePret} onProbleme={handleProbleme}
                                                showCommencer={true} showPret={false} isProblem={true} canAct={canAct} />
                                        ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* VERSION DESKTOP : Quatre colonnes - Optimisées */}
            <div className="hidden sm:grid grid-cols-4 gap-2 h-[calc(100vh-220px)]">
                {/* Colonne EN ATTENTE */}
                <div className="bg-amber-500/10 rounded-xl border border-amber-500/30 overflow-hidden flex flex-col min-w-0">
                    <div className="bg-amber-500 text-white px-3 py-2 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span className="font-bold text-xs">EN ATTENTE</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-sm font-bold px-2">
                            {pendingOrders.length}
                        </Badge>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                        <div className="space-y-2">
                            {pendingOrders.length === 0 ? (
                                <div className="text-center py-8 text-amber-600/50">
                                    <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Aucune commande</p>
                                </div>
                            ) : (
                                pendingOrders.map(order => (
                                    <KitchenTicket
                                        key={order.order_id}
                                        order={order}
                                        isFullscreen={isFullscreen}
                                        onCommencer={handleCommencer}
                                        onPret={handlePret}
                                        onProbleme={handleProbleme}
                                        showCommencer={true}
                                        showPret={false}
                                        canAct={canAct}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Colonne EN PRÉPARATION */}
                <div className="bg-blue-500/10 rounded-xl border border-blue-500/30 overflow-hidden flex flex-col min-w-0">
                    <div className="bg-blue-600 text-white px-3 py-2 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <Utensils className="w-4 h-4" />
                            <span className="font-bold text-xs">EN PRÉPARATION</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-sm font-bold px-2">
                            {preparingOrders.length}
                        </Badge>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                        <div className="space-y-2">
                            {preparingOrders.length === 0 ? (
                                <div className="text-center py-8 text-blue-600/50">
                                    <Utensils className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Aucune préparation</p>
                                </div>
                            ) : (
                                preparingOrders.map(order => (
                                    <KitchenTicket
                                        key={order.order_id}
                                        order={order}
                                        isFullscreen={isFullscreen}
                                        onCommencer={handleCommencer}
                                        onPret={handlePret}
                                        onProbleme={handleProbleme}
                                        showCommencer={false}
                                        showPret={true}
                                        canAct={canAct}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Colonne PRÊTES */}
                <div className="bg-green-500/10 rounded-xl border border-green-500/30 overflow-hidden flex flex-col min-w-0">
                    <div className="bg-green-600 text-white px-3 py-2 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-bold text-xs">PRÊTES</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-sm font-bold px-2">
                            {readyOrders.length}
                        </Badge>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                        <div className="space-y-2">
                            {readyOrders.length === 0 ? (
                                <div className="text-center py-8 text-green-600/50">
                                    <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Aucune commande prête</p>
                                </div>
                            ) : (
                                readyOrders.map(order => (
                                    <KitchenTicket
                                        key={order.order_id}
                                        order={order}
                                        isFullscreen={isFullscreen}
                                        onCommencer={handleCommencer}
                                        onPret={handlePret}
                                        onProbleme={handleProbleme}
                                        showCommencer={false}
                                        showPret={false}
                                        isReady={true}
                                        canAct={canAct}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Colonne PROBLÈMES */}
                <div className="bg-red-500/10 rounded-xl border border-red-500/30 overflow-hidden flex flex-col min-w-0">
                    <div className="bg-red-600 text-white px-3 py-2 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <AlertOctagon className="w-4 h-4" />
                            <span className="font-bold text-xs">PROBLÈMES</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-sm font-bold px-2">
                            {problemOrders.length}
                        </Badge>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                        <div className="space-y-2">
                            {problemOrders.length === 0 ? (
                                <div className="text-center py-8 text-red-600/50">
                                    <AlertOctagon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Aucun problème</p>
                                </div>
                            ) : (
                                problemOrders.map(order => (
                                    <KitchenTicket
                                        key={order.order_id}
                                        order={order}
                                        isFullscreen={isFullscreen}
                                        onCommencer={handleCommencer}
                                        onPret={handlePret}
                                        onProbleme={handleProbleme}
                                        showCommencer={true}
                                        showPret={false}
                                        isProblem={true}
                                        canAct={canAct}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialog Problème */}
            <Dialog open={problemDialog.open} onOpenChange={(open) => !open && setProblemDialog({ open: false, order: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertOctagon className="w-6 h-6" />
                            Signaler un problème
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-3">
                            Commande #{problemDialog.order?.order_number || problemDialog.order?.order_id?.slice(-4)} - Table {problemDialog.order?.table_number}
                        </p>
                        <Textarea
                            value={problemNote}
                            onChange={(e) => setProblemNote(e.target.value)}
                            placeholder="Décrivez le problème (rupture produit, erreur, etc.)"
                            rows={4}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setProblemDialog({ open: false, order: null })}>
                            Annuler
                        </Button>
                        <Button onClick={submitProbleme} className="bg-red-600 hover:bg-red-700">
                            Signaler le problème
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default KitchenDashboardPage;
